import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Comment } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { ApiError } from '@/lib/api';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ProfileAvatar } from '@/components/ProfileAvatar';
import { ThumbsDown, ThumbsUp, Trash2 } from 'lucide-react';
import { commentsApi } from './api';
import { useAuth } from '@/features/auth/AuthContext';

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function CommentRow({ c, slug, canModerate }: { c: Comment; slug: string; canModerate: boolean }) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['comments', slug] });
  const react = useMutation({
    mutationFn: (value: 1 | -1 | 0) => commentsApi.react(c.id, value),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: () => commentsApi.remove(c.id), onSuccess: invalidate });

  return (
    <li className="flex gap-3 py-4">
      <ProfileAvatar profile={{ name: c.authorName, avatarColor: '#475569', isKids: false }} size={36} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-ink">{c.authorName}</span>
          <span className="text-xs text-ink-faint">{timeAgo(c.createdAt)}</span>
        </div>
        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-ink-muted">{c.body}</p>
        <div className="mt-2 flex items-center gap-3 text-ink-faint">
          <button
            type="button"
            onClick={() => react.mutate(c.myReaction === 1 ? 0 : 1)}
            disabled={react.isPending}
            className={cn('inline-flex items-center gap-1 text-xs hover:text-ink', c.myReaction === 1 && 'text-brand-soft')}
          >
            <ThumbsUp size={15} /> {c.likes > 0 && c.likes}
          </button>
          <button
            type="button"
            onClick={() => react.mutate(c.myReaction === -1 ? 0 : -1)}
            disabled={react.isPending}
            className={cn('inline-flex items-center gap-1 text-xs hover:text-ink', c.myReaction === -1 && 'text-danger')}
          >
            <ThumbsDown size={15} /> {c.dislikes > 0 && c.dislikes}
          </button>
          {(c.mine || canModerate) && (
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="inline-flex items-center gap-1 text-xs hover:text-danger"
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

export function Comments({ slug }: { slug: string }) {
  const { user, activeProfile } = useAuth();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const canModerate = user?.role === 'owner' || user?.role === 'admin';

  const { data, isLoading } = useQuery({ queryKey: ['comments', slug], queryFn: () => commentsApi.list(slug) });

  const post = useMutation({
    mutationFn: () => commentsApi.post(slug, body.trim()),
    onSuccess: (res) => {
      setBody('');
      if (res.held) {
        setNotice("Your comment was blocked by our filter. If a moderator approves it, we'll email you.");
      } else {
        setNotice(null);
        void qc.invalidateQueries({ queryKey: ['comments', slug] });
      }
    },
  });

  const list = data?.comments ?? [];

  return (
    <section className="mt-12">
      <h2 className="font-display text-xl font-bold text-ink">
        Comments {list.length > 0 && <span className="text-ink-faint">({list.length})</span>}
      </h2>

      {data && !data.commentsEnabled ? (
        <p className="mt-4 text-sm text-ink-muted">Comments are turned off for this title.</p>
      ) : (
        <>
          {user ? (
            <div className="mt-4">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={2000}
                rows={3}
                placeholder={`Comment as ${activeProfile?.name ?? user.displayName}…`}
                className="w-full resize-y rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:border-brand focus:outline-none"
              />
              {post.isError && (
                <Alert tone="error" className="mt-2">
                  {post.error instanceof ApiError ? post.error.message : 'Could not post your comment.'}
                </Alert>
              )}
              {notice && <Alert tone="info" className="mt-2">{notice}</Alert>}
              <div className="mt-2 flex justify-end">
                <Button size="sm" onClick={() => post.mutate()} disabled={!body.trim() || post.isPending}>
                  {post.isPending ? 'Posting…' : 'Post comment'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-ink-muted">
              <Link to="/login" className="font-semibold text-brand-soft hover:text-brand">Sign in</Link> to join the conversation.
            </p>
          )}

          {isLoading ? (
            <p className="mt-6 text-sm text-ink-faint">Loading comments…</p>
          ) : list.length === 0 ? (
            <p className="mt-6 text-sm text-ink-faint">No comments yet — be the first.</p>
          ) : (
            <ul className="mt-4 divide-y divide-border">
              {list.map((c) => (
                <CommentRow key={c.id} c={c} slug={slug} canModerate={canModerate} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
